import { TAU, measureText, polar } from "./geometry";
import { CHART_CONFIG } from "./config";
import { swatch } from "./palette";

/**
 * The layout pass: turns the hierarchical data into a flat renderer-agnostic
 * "scene" — arcs, polylines and labels in absolute px of a fixed logical
 * viewport — which the SVG renderer draws verbatim and hitTest() picks
 * against. Ported from chart-poc's layout.ts (its three renderers shared this
 * exact scene); trimmed to the one config this view ships with.
 *
 * Ring 1 is always up; ring N+1 only appears once a segment on ring N is
 * selected, flying out from its parent ring while the camera zooms toward
 * the drilled segment.
 */

// 16:9-ish logical viewport — wide on purpose: the rings are bounded by the
// short side either way, and the extra width is all label room.
const VIEW_W = 2400;
const VIEW_H = 1350;
const CENTER_X = 1200;
const CENTER_Y = 675;

/** Ceiling on `ringCount` (which normally follows the data's depth) — past
 *  this the rings outgrow the fixed viewport. */
const MAX_RINGS = 8;

export const SCENE_SIZE = { width: VIEW_W, height: VIEW_H };

/**
 * Ring bands, innermost first: `radius` is the band's centre line and
 * `thickness` its width, both in scene px.
 *
 * `ringOffset` is the empty space between one ring's outer edge and the next
 * ring's inner edge, so the centre lines are a thickness further apart again.
 */
function ringsFor(config, shift) {
  const pitch = config.ringThickness + config.ringOffset;
  const count = Math.max(1, Math.min(Math.round(config.ringCount), MAX_RINGS));
  return Array.from({ length: count }, (_, i) => ({
    radius: config.baseRadius + shift + pitch * i,
    thickness: config.ringThickness,
  }));
}

/**
 * A branch weighs what its children weigh; only leaves carry their own value.
 * Parent values in the data are ignored, so they can never disagree with the
 * children they contain.
 */
export const nodeWeight = (n) =>
  n.children?.length ? n.children.reduce((t, c) => t + nodeWeight(c), 0) : n.value ?? 0;

/**
 * The count that prefixes a segment's label ("12 ONBOARDING FRICTION"), as its
 * own run so it can be set heavier than the label. Weights are counts in the
 * common case; a fractional one is shown to a single decimal rather than as a
 * long float. The trailing space is non-breaking: SVG collapses ordinary
 * whitespace at a tspan boundary, which would run the count into the label.
 */
const countRun = (weight, config) => {
  if (!config.showValues) return null;
  return `${Number.isInteger(weight) ? weight : Math.round(weight * 10) / 10}\u00A0`;
};

function place(nodes, start, end, depth, parentId, out, rings, segmentGap) {
  const total = nodes.reduce((t, n) => t + nodeWeight(n), 0);
  // A px gap converted per ring, so it reads the same width on every level
  // instead of fanning out with radius.
  const pad = segmentGap / rings[depth].radius;
  let cursor = start;
  for (const [index, node] of nodes.entries()) {
    // Kept on the record: the labels want it too, and it is not free to
    // recompute per node on every animated frame.
    const weight = nodeWeight(node);
    const span = ((end - start) * weight) / total;
    // The gap can swallow a near-zero segment whole — collapse it to a sliver
    // at its midpoint rather than letting the angles invert (an inverted arc
    // draws backwards, over its neighbours).
    const mid = cursor + span / 2;
    out.push({
      node,
      depth,
      index,
      parentId,
      weight,
      a0: Math.min(cursor + pad / 2, mid),
      a1: Math.max(cursor + span - pad / 2, mid),
    });
    if (node.children && depth + 1 < rings.length) {
      place(node.children, cursor, cursor + span, depth + 1, node.id, out, rings, segmentGap);
    }
    cursor += span;
  }
}

/**
 * @param data `{ children: [...] }` hierarchy (see buildDonutData).
 * @param scale Displayed px per scene px. Text is sized in scene units, so the
 * label size is divided by this to land at a fixed size on screen.
 * @param hover Per-segment hover progress, 0..1, keyed by node id.
 * @param reveal Per-segment selection progress, 0..1, keyed by node id.
 * @param activePath Selected segment per ring, outermost last. `['a']` means
 * ring-1 segment `a` is open and its children are showing on ring 2.
 */
export function buildScene(
  data,
  scale = 1,
  hover = {},
  reveal = {},
  activePath = [],
  config = CHART_CONFIG
) {
  const arcs = [];
  const lines = [];
  const labels = [];
  const arcLabels = [];
  const leads = [];
  /** Label size in scene units, so it lands at config.labelSize on screen. */
  const labelSizeScene = config.labelSize / scale;

  // How far each ring has been opened, keyed by the depth of the selected
  // segment. Read from the tween rather than the path, so closing eases out
  // instead of cutting. Two siblings can be mid-crossfade at once, so take
  // whichever leads. Computed before the rings because the whole chart can
  // pull inward by `drillShrink` per open level, riding the same tween.
  const opening = [];
  const walkOpen = (nodes, depth) => {
    for (const n of nodes) {
      const t = reveal[n.id] ?? 0;
      if (t > (opening[depth] ?? 0)) opening[depth] = t;
      if (n.children) walkOpen(n.children, depth + 1);
    }
  };
  walkOpen(data.children, 0);

  const RINGS = ringsFor(config, config.drillShrink * opening.reduce((t, v) => t + v, 0));
  const ringOuter = (depth) => RINGS[depth].radius + RINGS[depth].thickness / 2;

  const placed = [];
  const from = (config.startAngle * Math.PI) / 180;
  place(data.children, from, from + Math.PI * 2, 0, null, placed, RINGS, config.segmentGap);

  const alpha = (v) => Math.min(1, Math.max(0, v));

  // Camera: zoom toward the point of interest — the segment being drilled into
  // plus the child ring it reveals. Each drilled level blends toward its own
  // target with the same reveal tween that flies the ring out, so the zoom
  // tracks the drill and plays back out on collapse (nodes no longer selected
  // keep steering while their reveal decays). The scene stays concentric under
  // this transform, so applying it is just new centre + scaled radii; label
  // text, offsets and stroke widths stay screen-constant on purpose.
  let cx = CENTER_X;
  let cy = CENTER_Y;
  let zs = 1;
  if (config.zoomEnabled) {
    const drilled = placed
      .filter((p) => (reveal[p.node.id] ?? 0) > 0.001 && p.node.children && p.depth + 1 < RINGS.length)
      .sort((a, b) => a.depth - b.depth || reveal[a.node.id] - reveal[b.node.id]);
    let fx = CENTER_X;
    let fy = CENTER_Y;
    const targets = drilled.map((p) => ({
      T: zoomTarget(p, config),
      t: alpha(reveal[p.node.id] ?? 0),
    }));
    for (const { T, t } of targets) {
      zs = lerp(zs, T.s, t);
      fx = lerp(fx, T.fx, t);
      fy = lerp(fy, T.fy, t);
    }
    cx = (CENTER_X - fx) * zs + CENTER_X;
    cy = (CENTER_Y - fy) * zs + CENTER_Y;
    for (const r of RINGS) {
      r.radius *= zs;
      r.thickness *= zs;
    }
  }

  // Ring tracks: a dotted circle for each ring a selection has opened up, drawn
  // before the segments so they sit on top of it.
  for (let depth = 1; depth < RINGS.length; depth++) {
    const t = opening[depth - 1] ?? 0;
    if (t <= 0.001) continue;
    lines.push({
      points: circlePoints(cx, cy, RINGS[depth].radius),
      stroke: config.colorTrack,
      width: config.trackWidth,
      dash: [config.trackDash, config.trackGap],
      opacity: alpha(t),
    });
  }

  for (const p of placed) {
    const { depth } = p;
    // Ring 1 is always up; deeper rings fly out from their parent ring as the
    // selection settles. Visibility keys off the tween rather than the path, so
    // collapsing plays the flight back in instead of cutting to nothing.
    const t = depth === 0 ? 1 : reveal[p.parentId] ?? 0;
    if (t <= 0.001) continue;

    const activeHere = activePath[depth] ?? null;
    const isActive = p.node.id === activeHere;
    const hatch = activeHere !== null && !isActive;

    const radius =
      depth === 0 ? RINGS[0].radius : lerp(RINGS[depth - 1].radius, RINGS[depth].radius, t);
    const segmentOuter = radius + RINGS[depth].thickness / 2;

    // Hover and selection grow the segment by their own factors; whichever
    // asks for more wins, so hovering a selected segment never shrinks it.
    const grow = Math.max(
      (config.hoverScale - 1) * (hover[p.node.id] ?? 0),
      (config.selectedScale - 1) * (reveal[p.node.id] ?? 0)
    );
    const thickness = RINGS[depth].thickness * (1 + grow);

    arcs.push({
      id: p.node.id,
      depth,
      r0: radius - thickness / 2,
      r1: radius + thickness / 2,
      a0: p.a0,
      a1: p.a1,
      cornerRadius: config.cornerRadius * zs,
      fill: hatch
        ? config.hatchBg
        : activeHere !== null
          ? config.colorSelected
          : p.node.color ?? swatch(p.index),
      hatch,
      stroke: hatch ? config.hatchBorder : undefined,
      strokeWidth: hatch ? 1 : undefined,
      opacity: depth === 0 ? 1 : alpha(t),
      selectable: Boolean(p.node.children) && depth + 1 < RINGS.length,
    });

    // The drilled segment keeps its name ON the band — curved along the arc,
    // riding the same reveal that fades its leader label out below. Keyed off
    // the tween (any child-bearing node with reveal), so collapsing fades the
    // curved name back out instead of cutting it.
    const selT = alpha(reveal[p.node.id] ?? 0);
    if (selT > 0.001 && p.node.children) {
      const midA = (((p.a0 + p.a1) / 2) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
      // Bottom-half arcs run the path backwards so the text stays upright;
      // the baseline shifts across the centre line to keep it optically
      // centred in the band either way.
      const flipped = midA > Math.PI / 2 && midA < (3 * Math.PI) / 2;
      const baselineR = radius + (flipped ? 1 : -1) * labelSizeScene * 0.35;
      const tracking = labelSizeScene * config.labelTracking;
      const fitted = fitArcText(
        countRun(p.weight, config),
        p.node.label.toUpperCase(),
        labelSizeScene,
        config,
        tracking,
        baselineR * (p.a1 - p.a0) - labelSizeScene * 1.5
      );
      if (fitted) {
        const rel = (r, a) => [r * Math.sin(a), -r * Math.cos(a)];
        const [sx, sy] = rel(baselineR, flipped ? p.a1 : p.a0);
        const [ex, ey] = rel(baselineR, flipped ? p.a0 : p.a1);
        arcLabels.push({
          count: fitted.count,
          text: fitted.text,
          sx,
          sy,
          ex,
          ey,
          r: baselineR,
          largeArc: p.a1 - p.a0 > Math.PI ? 1 : 0,
          sweep: flipped ? 0 : 1,
          size: labelSizeScene,
          weight: config.labelWeight,
          countWeight: config.labelCountWeight,
          tracking,
          color: config.colorArcLabel,
          opacity: alpha(t) * selT,
        });
      }
    }

    // The segment you drilled into gives up its label to its children — faded
    // out by its own reveal, so it keeps its slot and the ones around it never
    // jump. The level you are looking at reads black; the ones behind it stay
    // muted, so with nothing open ring 1 starts out black.
    leads.push({
      count: countRun(p.weight, config),
      text: p.node.label,
      mid: (p.a0 + p.a1) / 2,
      color: depth === activePath.length ? config.colorActiveLabel : config.colorMutedLabel,
      opacity: alpha(t) * (1 - alpha(reveal[p.node.id] ?? 0)),
      // The anchor sits at the centre of the segment's outer edge; the bend is
      // filled in below, on the guide ring all labels share.
      anchorR: segmentOuter + config.labelStartOffset,
      bendR: 0,
      depth,
    });
  }

  // How far the outermost open ring has flown out.
  let outerEdge = ringOuter(0);
  for (let depth = 1; depth < RINGS.length; depth++) {
    const t = opening[depth - 1] ?? 0;
    if (t <= 0.001) break;
    outerEdge = lerp(outerEdge, ringOuter(depth), t);
  }

  // Every label bends on one shared guide ring — the one belonging to the
  // outermost revealed ring — so inner-ring arms are longer, and all of them
  // grow as another level opens (the POC's "extending-arm" variant; its
  // fixed-arm sibling would run leaders under newly revealed rings here).
  const shared = outerEdge + config.labelGuideOffset;
  for (const l of leads) l.bendR = shared;

  layoutLeads(leads, cx, cy, config, labelSizeScene, lines, labels);

  // Inner rings paint over outer ones — ring 1 on top, then ring 2, and so on.
  // The renderer draws in array order, so the deepest ring goes down first.
  arcs.sort((a, b) => b.depth - a.depth);

  return {
    width: VIEW_W,
    height: VIEW_H,
    cx,
    cy,
    arcs,
    lines,
    labels,
    arcLabels,
    hatch: { bg: config.hatchBg, line: config.hatchLine, tile: config.hatchTile },
  };
}

const lerp = (a, b, t) => a + (b - a) * t;

/**
 * The steady-state camera target for drilling into segment `p`: the active
 * segment plus the child ring it reveals, boxed by sampling both bounding
 * arcs, squared off, padded by `zoomBuffer`, and scaled to fit the viewport.
 * Built from the rings as they will sit once this drill settles (including
 * its share of drillShrink), so the target holds still while the tween runs.
 */
function zoomTarget(p, config) {
  const rings = ringsFor(config, config.drillShrink * (p.depth + 1));
  const r0 = rings[p.depth].radius - (rings[p.depth].thickness * config.selectedScale) / 2;
  const r1 = rings[p.depth + 1].radius + rings[p.depth + 1].thickness / 2;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i <= 32; i++) {
    const a = p.a0 + ((p.a1 - p.a0) * i) / 32;
    for (const r of [r0, r1]) {
      const [x, y] = polar(CENTER_X, CENTER_Y, r, a);
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
  }
  // The square is the tight box around the point of interest; the buffer only
  // widens what the camera frames around it, so the viewport shows square+buffer.
  const side = Math.max(maxX - minX, maxY - minY);
  return {
    s: Math.max(1, Math.min(VIEW_W, VIEW_H) / (side + config.zoomBuffer * 2)),
    fx: (minX + maxX) / 2,
    fy: (minY + maxY) / 2,
  };
}

function circlePoints(cx, cy, r) {
  const steps = 300;
  const pts = [];
  for (let i = 0; i <= steps; i++) pts.push(polar(cx, cy, r, (i / steps) * Math.PI * 2));
  return pts;
}

/**
 * Fit a count + label to the arc length available for an on-band curved
 * label. The count is measured at its own (heavier) weight and always kept —
 * it is the short, high-value half — so only the label is character-trimmed
 * to an ellipsis. Returns null when even a couple of label characters won't
 * fit; the zoomed segment is normally wide, so that only happens mid-flight
 * or on sliver segments.
 */
function fitArcText(count, text, size, config, tracking, available) {
  const countWidth = count ? measureText(count, size, config.labelCountWeight, tracking) : 0;
  const room = available - countWidth;
  if (room <= 0) return null;
  const fits = (s) => measureText(s, size, config.labelWeight, tracking) <= room;
  if (fits(text)) return { count, text };
  let cut = text;
  while (cut.length > 2 && !fits(`${cut.trimEnd()}…`)) cut = cut.slice(0, -1);
  const trimmed = `${cut.trimEnd()}…`;
  return fits(trimmed) ? { count, text: trimmed } : null;
}

/**
 * Greedy word-wrap against a px budget, capped at `maxLines`. Once the last
 * allowed line is reached it swallows the remaining words, and any line that
 * still overflows (that one, or a single word longer than the budget) is
 * character-trimmed to an ellipsis. SVG text has no native wrapping, so this
 * is where lines are decided; the renderer just stacks tspans.
 */
function wrapLabel(text, size, weight, tracking, maxWidth, maxLines, firstLineReserve = 0) {
  // The count run sits on the first line, at its own weight, so that line has
  // less room for label text than the ones below it.
  const fits = (s, lineIdx) =>
    measureText(s, size, weight, tracking) <= maxWidth - (lineIdx === 0 ? firstLineReserve : 0);
  const words = text.split(/\s+/).filter(Boolean);
  const out = [];
  let line = "";
  for (let i = 0; i < words.length; i++) {
    const cand = line ? `${line} ${words[i]}` : words[i];
    if (!line || fits(cand, out.length)) {
      line = cand;
      continue;
    }
    if (out.length === maxLines - 1) {
      line = `${line} ${words.slice(i).join(" ")}`;
      break;
    }
    out.push(line);
    line = words[i];
  }
  if (line) out.push(line);
  return out.map((l, i) => {
    if (fits(l, i)) return l;
    let cut = l;
    while (cut.length > 1 && !fits(`${cut.trimEnd()}…`, i)) cut = cut.slice(0, -1);
    return `${cut.trimEnd()}…`;
  });
}

/**
 * Weighted isotonic regression (pool-adjacent-violators): the non-decreasing
 * sequence closest to `targets` in weighted least squares. Exact and O(n) —
 * violating neighbours merge into pools that settle at their weighted mean.
 */
function isotonicNonDecreasing(targets, weights) {
  const pools = [];
  for (let i = 0; i < targets.length; i++) {
    let v = targets[i];
    let w = Math.max(weights[i], 1e-6);
    let n = 1;
    while (pools.length && pools[pools.length - 1].v >= v) {
      const p = pools.pop();
      v = (p.v * p.w + v * w) / (p.w + w);
      w += p.w;
      n += p.n;
    }
    pools.push({ v, w, n });
  }
  const out = [];
  for (const p of pools) for (let i = 0; i < p.n; i++) out.push(p.v);
  return out;
}

/**
 * Anchor, bend, label. The second arm is horizontal, at least `labelArmMin`
 * long and otherwise the longest wrapped line plus `labelArmPadding`, with
 * the text flush against its far end and stacked upward from the rule when
 * it wraps.
 *
 * Collision handling is exact, not nudged: per side, each label wants the
 * height where its segment's ray crosses the guide ring, subject to
 * consecutive labels sitting at least a block apart. Subtracting the
 * cumulative required separations turns that into plain monotonicity, which
 * isotonic regression solves with the least possible total movement —
 * untouched labels stay on their ray, crowded ones fan out symmetrically.
 * The bend then slides ALONG the guide circle to the resolved height, so a
 * displaced label's first arm tilts rather than its elbow leaving the ring.
 * Fading labels shrink their reserved space and pull with their opacity, so
 * neighbours glide back as a drilled label hands over to its children.
 */
function layoutLeads(leads, cx, cy, config, labelSize, lines, labels) {
  const tracking = labelSize * config.labelTracking;
  const lineHeight = labelSize * config.labelLineHeight;

  const items = [];
  for (const l of leads) {
    if ((l.opacity ?? 1) <= 0.01) continue; // invisible — reserves no slot
    const countWidth = l.count
      ? measureText(l.count, labelSize, config.labelCountWeight, tracking)
      : 0;
    const textLines = wrapLabel(
      l.text.toUpperCase(),
      labelSize,
      config.labelWeight,
      tracking,
      labelSize * config.labelMaxWidthEm,
      config.labelMaxLines,
      countWidth
    );
    // The arm must clear the widest line — and line 0 carries the count too.
    const width = Math.max(
      ...textLines.map(
        (t, i) => measureText(t, labelSize, config.labelWeight, tracking) + (i === 0 ? countWidth : 0)
      )
    );
    const bend = polar(cx, cy, l.bendR, l.mid);
    const dir = bend[0] >= cx ? 1 : -1;
    const midN = ((l.mid % TAU) + TAU) % TAU;
    items.push({
      l,
      textLines,
      width,
      anchor: polar(cx, cy, l.anchorR, l.mid),
      dir,
      // Top-to-bottom angular order for this side. Height on the guide circle
      // is strictly monotone in angle per side, so sorting by THIS (rather
      // than by the near-equal heights themselves, which tie near 12 and 6
      // o'clock) is exactly the order in which leaders cannot cross.
      angleKey: dir === 1 ? midN : TAU - midN,
      idealY: bend[1],
      height: (textLines.length * lineHeight + labelSize * 0.6) * Math.min(1, l.opacity),
      y: bend[1],
    });
  }

  for (const dir of [-1, 1]) {
    const side = items
      .filter((it) => it.dir === dir)
      .sort((a, b) => a.angleKey - b.angleKey);
    if (side.length < 2) continue;
    // Cumulative separations: y_i ≥ y_{i-1} + height_i  ⇔  (y_i − S_i) non-decreasing.
    const S = [0];
    for (let i = 1; i < side.length; i++) S.push(S[i - 1] + side[i].height);
    const resolved = isotonicNonDecreasing(
      side.map((it, i) => it.idealY - S[i]),
      side.map((it) => it.l.opacity)
    );
    side.forEach((it, i) => {
      it.y = resolved[i] + S[i];
    });
  }

  for (const it of items) {
    const { l, dir } = it;
    // Bend on the guide circle at the resolved height — but never BEHIND the
    // anchor: a slot pushed past the circle's top would otherwise slide the
    // elbow to the vertical axis and point the first arm backwards. Clamped,
    // those labels get a clean vertical riser instead.
    const dyc = it.y - cy;
    const circleX = cx + dir * Math.sqrt(Math.max(l.bendR * l.bendR - dyc * dyc, 0));
    const bx = dir === 1 ? Math.max(circleX, it.anchor[0]) : Math.min(circleX, it.anchor[0]);
    const arm = Math.max(config.labelArmMin, it.width + config.labelArmPadding);
    const lineEnd = bx + dir * arm;

    lines.push({
      points: [it.anchor, [bx, it.y], [lineEnd, it.y]],
      stroke: config.colorLeader,
      width: config.leaderWidth,
      opacity: l.opacity,
    });
    labels.push({
      lines: it.textLines,
      count: l.count,
      countWeight: config.labelCountWeight,
      x: lineEnd - dir * 2,
      // Baseline of the LAST line — the block grows upward from the rule.
      y: it.y - labelSize * config.labelBaseline,
      lineHeight,
      align: dir === 1 ? "end" : "start",
      size: labelSize,
      weight: config.labelWeight,
      tracking,
      color: l.color,
      opacity: l.opacity,
    });
  }
}
